import dayjs from "dayjs";
import { useRouter } from "next/navigation";
import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SingleDatePicker } from "staysure-component-library";
import { LocaleType } from "staysure-component-library/dist/types/commonTypes";

import LabelButtonWrapper from "@/components/atoms/button-wrapper/button-wrapper";
import withBrand from "@/components/with-brand";
import { ANNUAL_MULTI_TRIP, SINGLE_TRIP } from "@/constants";
import useUpdateTripDetails, {
  UpdateProps,
} from "@/hooks/api/quote/update-trip-details";
import { getDateLocaleCode } from "@/libs/i18n/config";
import { useWizardTranslation } from "@/libs/i18n/use-wizard-translation";
import { useCurrentLocale } from "@/libs/i18n/utils.client";
import { useLoading } from "@/providers/loading-provider";
import { ToggleModalContentType } from "@/schema/quote-page/page.schema";
import { RootState } from "@/store/root-reducer";
import { setApiError } from "@/store/slices/errors/api-error.slice";
import {
  setTripTypeChangedState,
  updateQuote,
} from "@/store/slices/quote/quote.slice";
import { QuoteData } from "@/types/quote/quote-data";
import { apiErrorMappings } from "@/utils/errors/api-error-map";

import Styles from "./st-amt-new-date.module.css";

type StAmtTogleModalProps = {
  brand?: "avanti" | "staysure";
  isOpenViaBottomToggle?: boolean;
  onCancel: () => void;
  onCancelModal?: () => void;
  onUpdated?: () => void;
};

function setPatchBody(
  attributes: QuoteData["attributes"],
  postData: UpdateProps["requestdata"]
) {
  let data = attributes.tripDetails;
  if (data) data = { ...data, ...postData };
  if (data?.policyType === SINGLE_TRIP) {
    delete data.area;
  }
  return data;
}

function StAmtTogleModalNewDate({
  onCancel,
  onCancelModal,
  onUpdated,
  stAmtToggleNewDateDatePickerPlaceHolder,
  stAmtToggleNewDateTitle,
  stAmtToggleNewDateCancelButtonLabel,
  stAmtToggleNewDateUpdateBtnLabel,
  isOpenViaBottomToggle,
  brand = "staysure",
}: Readonly<StAmtTogleModalProps & Partial<ToggleModalContentType>>) {
  const dispatch = useDispatch();
  const router = useRouter();
  const { t } = useWizardTranslation();
  const { showLoading, hideLoading } = useLoading();
  const { attributes } = useSelector((state: RootState) => state.quote);
  const { tripDetails } = attributes;

  const [isDateError, setIsDateError] = React.useState(false);
  const [date, setDate] = React.useState<string | null>(null);
  const [updateTriDetails, setUpdateTriDetails] = React.useState(false);
  const locale = useCurrentLocale();

  useEffect(() => {
    if (tripDetails) {
      if (
        tripDetails.policyPackage &&
        tripDetails.policyType === ANNUAL_MULTI_TRIP &&
        tripDetails.startDate
      ) {
        setDate(tripDetails.startDate);
      }
    }
  }, [tripDetails]);

  const dateValidate = (dateValue: Date) => {
    let validate = false;
    if (
      dateValue instanceof Date &&
      (Number.isNaN(dateValue.getDay()) ||
        Number.isNaN(dateValue.getMonth()) ||
        Number.isNaN(dateValue.getFullYear()))
    ) {
      validate = true;
    }

    if (
      dateValue < new Date(Date.now() - 1 * 24 * 60 * 60 * 1000) ||
      dateValue > new Date(Date.now() + 89 * 24 * 60 * 60 * 1000)
    ) {
      validate = true;
    }

    return validate;
  };

  const handleOnBlur = () => {
    if (date) {
      const dateValue = new Date(date);
      if (dateValidate(dateValue)) {
        setIsDateError(true);
      }
    }
  };

  const handleDateChange = (val: Date) => {
    setIsDateError(false);
    if (val !== null) {
      setDate(dayjs(val).format("YYYY-MM-DD").toString());
    } else {
      setDate(null);
    }
    if (!dateValidate(val)) {
      setDate(
        `${val.getFullYear()}-${val.getMonth() < 9 ? "0" : ""}${
          val.getMonth() + 1
        }-${val.getDate() < 10 ? "0" : ""}${val.getDate()}`
      );
    }
  };

  /* istanbul ignore next */
  const resetTrigger = useCallback(() => {
    setUpdateTriDetails(false);
    hideLoading();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { data, loading, error, updateTripDetails } = useUpdateTripDetails({
    resetTrigger,
  });

  const TabToggleHandler = () => {
    const requestdata = {
      startDate: date ?? "",
      policyType: ANNUAL_MULTI_TRIP,
      initialQuotePageLoading: true,
    };

    const bodyData = setPatchBody(attributes, requestdata);
    updateTripDetails(bodyData);
  };

  useEffect(() => {
    const handleEffect = async () => {
      if (!loading && data && !error) {
        dispatch(updateQuote(data));
        if (isOpenViaBottomToggle) {
          dispatch(setTripTypeChangedState(true));
        }
        onCancel();
      } else if (!loading && error !== null) {
        if (error.response?.status === 401) {
          router.push("/");
        } else {
          onCancel();
          const errorObject = await apiErrorMappings(error);
          errorObject.multiButton = true;
          dispatch(setApiError(errorObject));
        }
      }
    };
    handleEffect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading, error, dispatch, onCancel, router]);

  const handleUpdate = () => {
    if (date) {
      TabToggleHandler();
      showLoading();
      if (onUpdated !== undefined) {
        onUpdated();
      }
    } else {
      setIsDateError(true);
    }
  };

  if (updateTriDetails) {
    return null;
  }

  return (
    <div data-testid="st-amt-new-date-modal-id">
      <div>
        <h1 className="text-text-title mb-spacing-l md:text-[31px] text-[23px] font-header-font font-[400] leading-[28px] md:leading-[34px]">
          {stAmtToggleNewDateTitle}
        </h1>
      </div>
      <div className={`${Styles.singleDatePicker} mb-spacing-l relative`}>
        <SingleDatePicker
          disablePastDates
          maximumDate={new Date(Date.now() + 89 * 24 * 60 * 60 * 1000)}
          customizedMessage={t(
            "future_start_date_error",
            "Please select when you would like your policy to start (up to 90 days from today)"
          )}
          defaultDate={date && date !== "" ? new Date(date) : undefined}
          isError={isDateError}
          label={stAmtToggleNewDateDatePickerPlaceHolder ?? "From"}
          onChange={(e: Date) => handleDateChange(e as Date)}
          onBlur={handleOnBlur}
          brand={brand}
          locale={getDateLocaleCode(locale) as LocaleType}
        />
      </div>

      <div className="md:flex md:justify-between ">
        <LabelButtonWrapper
          isDisabled={isDateError}
          id="st-amt-toggle-modal-update-button"
          hierarchy="primary"
          label={stAmtToggleNewDateUpdateBtnLabel ?? "Update"}
          className="md:w-[148px] w-full mr-[0px] mb-[8px] md:mb-[0px] text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] text-text-on-primary"
          onClick={() => {
            handleUpdate();
          }}
        />

        <LabelButtonWrapper
          id="st-amt-toggle-modal-cancel-button"
          hierarchy="secondary"
          label={stAmtToggleNewDateCancelButtonLabel ?? "Cancel"}
          className="md:w-[148px] w-full text-[18px] font-body-font h-[48px] max-h-[48px] font-[600] leading-[100%] text-interactive-secondary-default"
          onClick={() => {
            (onCancelModal ?? onCancel)?.();
          }}
        />
      </div>
    </div>
  );
}

export default withBrand(StAmtTogleModalNewDate);
